
































































import Vue from "vue";
import { computed, ref } from "@vue/composition-api";
import CategoryBarRow from "./CategoryBarRow.vue";
import CountColumnHeader from "./CountColumnHeader.vue";
import GenAiCard from "./GenAiCard.vue";
import GenAiDonut from "./GenAiDonut.vue";
import LevelSlider from "./LevelSlider.vue";
import ViewModeToggle from "./ViewModeToggle.vue";
import SkeletonLoaderItem from "@/components/SkeletonLoaderItem.vue";
import { LOAD_FAILED_TEXT, state } from "../data/state";
import { STOP_TEXT, use_people_stop } from "../data/stops";
import { CategoryCount, INFO_CATEGORIES, SENSITIVITY_STOPS } from "../data/model";
import { SIZE, TYPE, useGenAiPalette } from "../data/design";
import { show_gen_ai_employees } from "../data/drilldown";
import { use_view_mode } from "../data/view-mode";


/**
 * What the card counts at each ruler stop, for the line under its title. The
 * ruler opens on "Any Content", which includes unclassified content, so the
 * title cannot say "sensitive information" (frontend fix list F5, Oct 2026):
 * the title names the people, this line names the content.
 */
const SCOPE_TEXT: Record<string, string> = {
  high: "About high-sensitivity information",
  medium: "About high and medium sensitivity information",
  low: "About high, medium and low sensitivity information",
  none: "About any content, including unclassified",
};

/**
 * Widget 5 — "Employees Interacting with AI" (gen-ai-ui's "Employees
 * Interacted with AI about Sensitive Information", Sep 2026).
 *
 * People, not activities: each row is the employees whose AI activity touched
 * that category, split by the sensitivity of the content. One person can land
 * in several categories, so the rows overlap. The counts come aggregated by
 * employee type (tier1count, as intel-api defines the coordinate), so nothing
 * dedupes across categories: the headline is the largest category, a floor.
 *
 * Ami's spec for this widget was still to come ("Widget 5: i will update"),
 * so the queries behind it are the most direct reading of the prototype.
 */
export default Vue.extend({
  components: {
    CategoryBarRow,
    CountColumnHeader,
    GenAiCard,
    GenAiDonut,
    LevelSlider,
    ViewModeToggle,
    SkeletonLoaderItem,
  },
  setup() {
    const palette = useGenAiPalette();
    const view = use_view_mode("riskyEmployees");

    const is_loading = computed(() => state.loading.value.is_loading);

    /** opens on "Any Content", so the card shows the full picture first */
    const level = ref(SENSITIVITY_STOPS.length);
    const active_keys = computed(() => SENSITIVITY_STOPS.slice(0, level.value));
    // each stop is its own query: the people per category, and in all
    const at = use_people_stop(level);

    /** every category always has a row, as the prototype lists them — a
     *  tenant with no activity yet shows six zeros rather than a blank card */
    const items = computed<CategoryCount[]>(() =>
      INFO_CATEGORIES.map((c) => ({
        ...c,
        count: at.data.value ? at.data.value.rows[c.id] || 0 : 0,
      }))
    );

    const total_for = (item: CategoryCount) => item.count;

    /** the largest category: the counts cannot be deduplicated across them */
    const headline = computed(() => (at.data.value ? at.data.value.total : 0));

    /** a failed request has no count: a dash, never a zero */
    const load_failed = computed(() => state.failed.value.interactions || at.failed.value);
    const stop_loading = computed(() => !is_loading.value && at.loading.value);

    /** the widest stop the ruler includes names the scope */
    const scope_text = computed(
      () => SCOPE_TEXT[active_keys.value[active_keys.value.length - 1]] || ""
    );

    /** scale every bar against the widest row at the CURRENT stop, so narrowing
     *  the ruler re-scales instead of leaving every bar stumpy */
    const max_value = computed(() =>
      Math.max(1, ...items.value.map((d) => total_for(d)))
    );

    /** one segment: a stop's count is not split by sensitivity */
    const segments_for = (item: CategoryCount) => [
      {
        key: at.stop.value,
        value: item.count,
        color:
          at.stop.value === "all"
            ? palette.value.categoryChart[item.id]
            : palette.value.sensitivity[at.stop.value].bar,
        title: `${STOP_TEXT[at.stop.value]}: ${item.count} employees`,
      },
    ];

    const aria_for = (item: CategoryCount) =>
      `Open the ${total_for(item)} employees who interacted with AI about ` +
      `${item.name} information, ${STOP_TEXT[at.stop.value].toLowerCase()}.`;

    const donut_items = computed(() =>
      items.value.map((item) => ({
        label: item.name,
        value: total_for(item),
        color: palette.value.categoryChart[item.id],
      }))
    );

    /** this card counts people, so its rows open the Employees list, not the
     *  events one — the number said 24 people, the list has to agree */
    const show_employees = (item?: CategoryCount) =>
      item &&
      show_gen_ai_employees(
        `<b>${item.name}</b>: Employee AI Interactions`,
        item.id,
        active_keys.value
      );

    return {
      load_failed,
      stop_loading,
      LOAD_FAILED_TEXT,
      scope_text,
      view,
      items,
      is_loading,
      level,
      headline,
      total_for,
      segments_for,
      max_value,
      aria_for,
      donut_items,
      show_employees,
      // gen-ai-ui SensitivityRuler: classifications, not risk levels
      stops: [
        { label: "High", pill: "High Sensitivity", key: "high" },
        { label: "+ Medium", pill: "High + Medium", key: "medium" },
        { label: "+ Low", pill: "High + Med + Low", key: "low" },
        { label: "All", pill: "Any Content", key: "none" },
      ],
      T: palette,
      TY: TYPE,
      S: SIZE,
    };
  },
});
